import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';

import { Button } from '@/shared/components/Button/Button';
import { ArrowBottom } from '@/shared/components/ArrowBottom/ArrowBottom';
import { renderWithBr } from '@/shared/lib/renderWithBr';
import { useLoginUrl } from '@/shared/hooks/useLoginUrl';
import { useLoginwallMainSection } from '@/shared/hooks/useLoginwallMainSection';
import { useYangoOnboardingConfig } from '@/shared/hooks/useYangoOnboardingConfig';
import { ShowcaseId } from '@/shared/lib/analytics/showcase';

import { getLoginwallFallbackTexts } from '../../lib/getLoginwallFallbackTexts';
import { HomeShowcase } from '../HomeShowcase/HomeShowcase';
import { HomeShowcaseSection } from '../HomeShowcaseSection/HomeShowcaseSection';
import { HomeShowcaseHeader } from '../HomeShowcaseHeader/HomeShowcaseHeader';
import { StaticCover } from '../StaticCover/StaticCover';
import { useI18n } from '../../hooks/useOnboardingI18n';

import styles from './LoginwallMainSection.module.css';

export const LoginwallMainSection = () => {
    const { backgroundUrl, title, subtitle, loginwallShowcaseId, isLoginButtonEnabled } =
        useLoginwallMainSection();
    const onboardingConfig = useYangoOnboardingConfig();
    const isLoginwallShowcaseEnabled = onboardingConfig?.isShowcaseEnabled ?? true;
    const isArrowButtonEnabled = onboardingConfig?.isArrowButtonEnabled ?? true;
    const i18n = useI18n();
    const fallbackTexts = getLoginwallFallbackTexts(i18n);

    const mainRef = useRef<HTMLDivElement>(null);

    const scrollToNextSection = useCallback(() => {
        const heightMain =
            (mainRef.current && mainRef.current.offsetHeight + mainRef.current.offsetTop) || 0;

        window.scrollTo({ top: heightMain, behavior: 'smooth' });
    }, [mainRef]);

    const loginUrl = useLoginUrl();

    return (
        <div className={classnames(styles.root)}>
            <div className={styles.loginBlock} ref={mainRef}>
                <StaticCover backgroundUrl={backgroundUrl} />
                <div className={styles.loginWrapper}>
                    <h1 className={styles.title}>{renderWithBr(title, fallbackTexts.title)}</h1>
                    <p className={styles.subTitle}>
                        {renderWithBr(subtitle, fallbackTexts.subtitle)}
                    </p>
                    {isLoginButtonEnabled && (
                        <Button
                            as="a"
                            variant="accent"
                            size={60}
                            href={loginUrl.toString()}
                            className={classnames(styles.loginButton)}
                            data-test-id="LoginwallLoginButton"
                        >
                            {i18n('loginwall_button')}
                        </Button>
                    )}
                </div>
                {isArrowButtonEnabled && (
                    <div className={styles.arrowBottom}>
                        <ArrowBottom
                            onScrollTo={scrollToNextSection}
                            withAnimation={false}
                            title={i18n('onboarding__arrow_button_text')}
                        />
                    </div>
                )}
            </div>
            {isLoginwallShowcaseEnabled && (
                <HomeShowcaseSection className={styles.showcase}>
                    <HomeShowcase
                        id={loginwallShowcaseId ?? ShowcaseId.LOGINWALL}
                        header={<HomeShowcaseHeader />}
                        footer={
                            isLoginButtonEnabled ? (
                                <div className={classnames(styles.loginInset)}>
                                    <Button
                                        as="a"
                                        variant="accent"
                                        size={60}
                                        href={loginUrl.toString()}
                                        className={classnames(styles.loginButton)}
                                    >
                                        {i18n('loginwall_button')}
                                    </Button>
                                </div>
                            ) : undefined
                        }
                    />
                </HomeShowcaseSection>
            )}
        </div>
    );
};
